import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminSkeleton } from "@/components/FeedSkeleton";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Flag, HeartHandshake, EyeOff, Trash2, ArrowRight, AlertTriangle, RefreshCw, Inbox } from "lucide-react";

const fmtDate = (s) => { try { return new Date(s).toLocaleString(); } catch { return ""; } };

const CountCard = ({ icon: Icon, label, value, tone, hint, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className="yalola-card p-4 text-left flex items-start gap-3 transition hover:shadow-md w-full"
    data-testid={testId}
  >
    <span className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ background: `${tone}1A`, color: tone }}>
      <Icon size={20} />
    </span>
    <div className="min-w-0">
      <div className="text-2xl font-bold leading-tight" style={{ fontFamily: "Outfit" }}>{value}</div>
      <div className="text-sm font-medium">{label}</div>
      {hint && <div className="text-xs mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{hint}</div>}
    </div>
  </button>
);

export const ModerationTriagePanel = ({ canManage = true }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/moderation/overview")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const prayerAct = async (item, action) => {
    try {
      await api.post(`/admin/content/prayer/${item.id}/action`, { action });
      toast.success(action === "delete" ? "Prière supprimée" : "Prière masquée");
      setToDelete(null);
      load();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const openReport = (r) => {
    const p = new URLSearchParams({ focus_type: r.target_type, focus_id: r.target_id });
    if (r.queue_status) p.set("focus_status", r.queue_status);
    if (r.queue_page) p.set("focus_page", String(r.queue_page));
    navigate(`/admin/moderation?${p.toString()}`);
  };

  if (loading && !data) return <AdminSkeleton />;
  const c = data?.counts || {};
  const reports = data?.recent_reports || [];
  const prayers = data?.recent_prayers || [];

  return (
    <div className="space-y-4" data-testid="admin-triage-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}><Inbox size={20} color="#2D5A4C" /> À traiter</h2>
          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>Tout ce qui demande ton attention, regroupé : signalements en attente, prières visibles sur le mur et contenus masqués.</p>
        </div>
        <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={load} disabled={loading} data-testid="triage-refresh"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <CountCard icon={Flag} label="Signalements en attente" value={c.reports_pending ?? 0} tone="#DC2626" hint="Signalés par les membres" onClick={() => navigate("/admin/moderation")} testId="triage-count-reports" />
        <CountCard icon={HeartHandshake} label="Prières à surveiller" value={c.prayers_visible ?? 0} tone="#2D5A4C" hint="Visibles sur le mur" onClick={() => navigate("/admin/contents?ct=prayers")} testId="triage-count-prayers" />
        <CountCard icon={EyeOff} label="Contenus masqués" value={c.content_hidden ?? 0} tone="#92400E" hint="Publications / stories / prières" onClick={() => navigate("/admin/contents")} testId="triage-count-hidden" />
      </div>

      <div className="yalola-card p-4 space-y-3" data-testid="triage-reports">
        <div className="flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2"><Flag size={16} color="#DC2626" /> Signalements récents</h3>
          <button className="text-sm font-medium flex items-center gap-1 hover:underline" style={{ color: "var(--yalola-primary)" }} onClick={() => navigate("/admin/moderation")} data-testid="triage-reports-all">Voir tout <ArrowRight size={14} /></button>
        </div>
        {reports.length === 0 && <div className="text-sm py-4 text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="triage-reports-empty">Aucun signalement en attente 🎉</div>}
        {reports.map((r) => (
          <div key={r.id} className="flex items-start gap-3 rounded-xl border p-3" style={{ borderColor: "var(--yalola-border)" }} data-testid={`triage-report-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="chip text-[10px]">{r.type_label}</span>
                {r.priority === "critical" && <span className="chip text-[10px] flex items-center gap-1" style={{ background: "#FEE2E2", color: "#B91C1C" }}><AlertTriangle size={11} /> Urgent</span>}
              </div>
              <div className="text-sm mt-1 line-clamp-2 break-words">{r.snippet || <span style={{ color: "var(--yalola-text-muted)" }}>—</span>}</div>
              <div className="text-xs mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{r.reason ? `${r.reason} · ` : ""}{fmtDate(r.created_at)}</div>
            </div>
            <Button size="sm" className="rounded-full shrink-0" style={{ background: "#DC2626", color: "#fff" }} onClick={() => openReport(r)} data-testid={`triage-report-open-${r.id}`}>Traiter</Button>
          </div>
        ))}
      </div>

      <div className="yalola-card p-4 space-y-3" data-testid="triage-prayers">
        <div className="flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2"><HeartHandshake size={16} color="#2D5A4C" /> Prières récentes</h3>
          <button className="text-sm font-medium flex items-center gap-1 hover:underline" style={{ color: "var(--yalola-primary)" }} onClick={() => navigate("/admin/contents?ct=prayers")} data-testid="triage-prayers-all">Voir tout <ArrowRight size={14} /></button>
        </div>
        {prayers.length === 0 && <div className="text-sm py-4 text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="triage-prayers-empty">Aucune prière sur le mur.</div>}
        {prayers.map((p) => (
          <div key={p.id} className="flex items-start gap-3 rounded-xl border p-3" style={{ borderColor: "var(--yalola-border)" }} data-testid={`triage-prayer-${p.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm line-clamp-2 break-words">{p.content}</div>
              <div className="text-xs mt-1 flex items-center gap-1.5 flex-wrap" style={{ color: "var(--yalola-text-muted)" }}>
                <span>{p.is_anonymous ? "Anonyme" : (p.author?.name || "—")}</span>
                <span>· {fmtDate(p.created_at)}</span>
                {p.is_urgent && <span className="chip text-[10px]" style={{ background: "#FEE2E2", color: "#B91C1C" }}>Urgent</span>}
              </div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => prayerAct(p, "hide")} disabled={!canManage} data-testid={`triage-prayer-hide-${p.id}`}><EyeOff size={13} /></Button>
              <Button size="sm" variant="outline" className="rounded-full" style={{ color: "#B91C1C" }} onClick={() => setToDelete(p)} disabled={!canManage} data-testid={`triage-prayer-delete-${p.id}`}><Trash2 size={13} /></Button>
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent data-testid="triage-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette prière ?</AlertDialogTitle>
            <AlertDialogDescription>La prière et toutes ses intercessions/commentaires seront définitivement supprimés. L'auteur sera notifié.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="triage-delete-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => toDelete && prayerAct(toDelete, "delete")} data-testid="triage-delete-confirm" style={{ background: "#B91C1C" }}>Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ModerationTriagePanel;
